/**
 * Members service — MOCK (CT-5563 shared data layer).
 *
 * Backs the Members list (stats, filters, bulk actions), the Invite / Bulk upload
 * wizards and the Member detail. No backend yet (contract TBD with ARP/backend):
 * state lives in the shared mock store, responses use the platform envelope
 * `{ status_code, body, meta }`, bodies are snake_case.
 *
 * Rules enforced here (not only in the UI): the Owner can never be suspended,
 * removed, deleted or have their user type changed.
 */
import {
  envelope,
  errorEnvelope,
  matchesSearch,
  notFoundEnvelope,
  paginate,
  sortItems,
} from '../shared/utils/mockServiceHelpers.js';
import { isMockImportActive, withMockQa } from './mockQaSwitches.js';
import { INVITATION_TTL_DAYS } from './seeds/members.js';
import { getMemberStudioGrants } from './studiosService.js';
import { nextId, resetStore, store } from './seeds/store.js';

export const MEMBER_STATUSES = [
  'active',
  'invited',
  'expired',
  'not_invited',
  'suspended',
  'removed',
];
export const MEMBER_USER_TYPES = ['owner', 'admin', 'member'];
export const MEMBER_STATUS_ACTIONS = ['suspend', 'reactivate', 'remove', 'revoke', 'resend'];
const ASSIGNABLE_USER_TYPES = ['admin', 'member'];
const PENDING_STATUSES = ['invited', 'expired', 'not_invited'];
const DAY = 86_400_000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const changeListeners = new Set();

/**
 * Subscribe to member mutations (invite, revoke, remove, status, type, profile), so the list
 * and the header indicators refresh live. Returns the unsubscribe function.
 * TODO(CT-5609 / backend): replaced by query invalidation / a push channel once the API exists.
 */
export const onMembersChanged = (listener) => {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
};

export const notifyMembersChanged = () => changeListeners.forEach((listener) => listener());

/** Test-only: reset the shared mock store (members, roles, groups, grants). */
export const __resetMockMembersForTests = () => {
  changeListeners.clear();
  resetStore();
};

const ownerForbidden = (what) =>
  errorEnvelope(403, 'OWNER_PROTECTED', `The workspace owner cannot be ${what}.`);

const nameOf = (m) => (m.name ? m.name : m.email);

export const decorateMember = (m) => ({
  ...m,
  display_name: nameOf(m),
  roles: m.role_ids
    .map((id) => store.roles.find((r) => r.id === id))
    .filter(Boolean)
    .map(({ id, name }) => ({ id, name })),
  groups: m.group_ids
    .map((id) => store.groups.find((g) => g.id === id))
    .filter(Boolean)
    .map(({ id, name }) => ({ id, name })),
});

const findMember = (id) => store.members.find((m) => m.id === id);

const ADDED_WINDOWS = { last_7_days: 7, last_30_days: 30, last_90_days: 90 };

const isSameLocalDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

const matchesAdded = (member, added) => {
  if (!added) return true;
  if (added === 'today') return isSameLocalDay(member.added_at, Date.now());
  const ageDays = (Date.now() - new Date(member.added_at).getTime()) / DAY;
  if (added === 'earlier') return ageDays > 30;
  if (added === 'older_than_90_days') return ageDays > 90;
  return ageDays <= (ADDED_WINDOWS[added] ?? Infinity);
};

const toArray = (v) => (v == null || v === '' ? [] : Array.isArray(v) ? v : [v]);

const matchesMemberFilters = (m, f = {}) => {
  const statuses = toArray(f.status);
  const types = toArray(f.user_type);
  const roles = toArray(f.role);
  const groups = toArray(f.group);
  if (statuses.length && !statuses.includes(m.status)) return false;
  if (types.length && !types.includes(m.user_type)) return false;
  if (roles.length && !roles.some((r) => m.role_ids.includes(r))) return false;
  if (groups.length && !groups.some((g) => m.group_ids.includes(g))) return false;
  if (f.mfa === 'on' && !m.mfa_enabled) return false;
  if (f.mfa === 'off' && m.mfa_enabled) return false;
  if (f.import_batch_id && m.import_batch_id !== f.import_batch_id) return false;
  return matchesAdded(m, f.added);
};

const SORT_ACCESSORS = {
  name: (m) => nameOf(m),
  email: (m) => m.email,
  user_type: (m) => m.user_type,
  status: (m) => m.status,
  mfa_enabled: (m) => (m.mfa_enabled ? 1 : 0),
  last_active_at: (m) => m.last_active_at,
  added_at: (m) => m.added_at,
  role_count: (m) => m.role_ids.length,
  group_count: (m) => m.group_ids.length,
};

/** Fields a free-text search looks at when `search.fields` is not given. */
const SEARCH_FIELDS = [
  'name',
  'email',
  'job_title',
  'department',
  'user_type',
  'status',
  (m) => m.role_ids.map((id) => store.roles.find((r) => r.id === id)?.name),
  (m) => m.group_ids.map((id) => store.groups.find((g) => g.id === id)?.name),
];

/**
 * List members (paginated, sorted, filtered, searched over every field).
 * TODO(CT-5563 / backend): `GET /people-access/members` — contract TBD (ARP/backend).
 */
export const listMembers = (params = {}) =>
  withMockQa('list', () => {
    const {
      page = 1,
      page_size = 10,
      order_by = 'name',
      order_direction = 'asc',
      filters = {},
      search = {},
    } = params;
    const fields = search.fields?.length ? search.fields : SEARCH_FIELDS;
    const matched = store.members
      .filter((m) => matchesMemberFilters(m, filters))
      .filter((m) => matchesSearch(m, search.query, fields));
    const sorted = sortItems(
      matched,
      SORT_ACCESSORS[order_by] ?? SORT_ACCESSORS.name,
      order_direction
    );
    const { items, meta } = paginate(sorted, { page, page_size });
    return Promise.resolve(
      envelope(
        { items: items.map(decorateMember), ...meta },
        { ...meta, order_by, order_direction }
      )
    );
  });

/**
 * One member.
 * TODO(CT-5563 / backend): `GET /people-access/members/:id` — contract TBD (ARP/backend).
 */
export const getMember = (id) => {
  const member = findMember(id);
  return Promise.resolve(
    member ? envelope(decorateMember(member)) : notFoundEnvelope('Member not found')
  );
};

/**
 * Header cards of the Members list.
 * TODO(CT-5563 / backend): `GET /people-access/members/stats` — contract TBD (ARP/backend).
 */
export const getMemberStats = () =>
  withMockQa('stats', () => {
    const active = store.members.filter((m) => m.status === 'active');
    return Promise.resolve(
      envelope({
        active: active.length,
        pending_invitations: store.members.filter((m) => PENDING_STATUSES.includes(m.status))
          .length,
        not_sent: store.members.filter((m) => m.status === 'not_invited').length,
        active_without_mfa: active.filter((m) => !m.mfa_enabled).length,
        total: store.members.length,
      })
    );
  });

/**
 * Which of these addresses already belong to the workspace (any status except removed).
 * TODO(CT-5610 / backend): `POST /people-access/members/lookup` — contract TBD (ARP/backend).
 */
export const findExistingMembers = (emails = []) => {
  const wanted = new Set(emails.map((e) => String(e).trim().toLowerCase()));
  const items = store.members
    .filter((m) => m.status !== 'removed' && wanted.has(m.email.toLowerCase()))
    .map(decorateMember);
  return Promise.resolve(envelope({ items, total: items.length }));
};

const inviteFields = (send) => {
  const now = Date.now();
  return send
    ? {
        status: 'invited',
        invited_at: new Date(now).toISOString(),
        invitation_expires_at: new Date(now + INVITATION_TTL_DAYS * DAY).toISOString(),
      }
    : { status: 'not_invited', invited_at: null, invitation_expires_at: null };
};

const unknownIds = (ids, collection) => ids.filter((id) => !collection.some((x) => x.id === id));

/**
 * Create members in bulk (Invite wizard and Bulk upload). `send_invitation`
 * decides `invited` vs `not_invited`; shared `role_ids` / `group_ids` apply to
 * every row unless the row sets its own. Rows with a bad or duplicate email are
 * returned in `rejected` instead of failing the batch.
 * TODO(CT-5563 / backend): `POST /people-access/members/bulk` — contract TBD (ARP/backend).
 */
export const createMembers = (payload = {}) => {
  const {
    members = [],
    send_invitation = true,
    import_batch = null,
    role_ids = [],
    group_ids = [],
  } = payload;
  const import_batch_id = import_batch?.id ?? payload.import_batch_id ?? null;
  const roleError = unknownIds(role_ids, store.roles);
  const groupError = unknownIds(group_ids, store.groups);
  if (roleError.length || groupError.length) {
    return Promise.resolve(
      errorEnvelope(
        422,
        'UNKNOWN_REFERENCE',
        `Unknown role or group: ${[...roleError, ...groupError].join(', ')}`
      )
    );
  }
  const created = [];
  const rejected = [];
  const seen = new Set(store.members.map((m) => m.email.toLowerCase()));
  members.forEach((row) => {
    const email = String(row.email ?? '')
      .trim()
      .toLowerCase();
    if (!EMAIL_RE.test(email))
      return rejected.push({ email: row.email ?? '', reason: 'invalid_email' });
    if (seen.has(email)) return rejected.push({ email, reason: 'duplicate' });
    const rowRoles = row.role_ids ?? role_ids;
    const rowGroups = row.group_ids ?? group_ids;
    if (unknownIds(rowRoles, store.roles).length || unknownIds(rowGroups, store.groups).length)
      return rejected.push({ email, reason: 'unknown_reference' });
    seen.add(email);
    const first = row.first_name ?? null;
    const last = row.last_name ?? null;
    const requestedType = row.user_type ?? 'member';
    const member = {
      id: nextId('mbr'),
      first_name: first,
      last_name: last,
      name: [first, last].filter(Boolean).join(' '),
      email,
      job_title: row.job_title ?? null,
      department: row.department ?? null,
      user_type: ASSIGNABLE_USER_TYPES.includes(requestedType) ? requestedType : 'member',
      mfa_enabled: false,
      last_active_at: null,
      added_at: new Date().toISOString(),
      suspended_at: null,
      removed_at: null,
      role_ids: [...rowRoles],
      group_ids: [...rowGroups],
      import_batch_id,
      ...inviteFields(send_invitation),
    };
    store.members = [member, ...store.members];
    created.push(decorateMember(member));
  });
  if (created.length && import_batch_id && !store.importBatches.some((b) => b.id === import_batch_id)) {
    store.importBatches = [
      {
        id: import_batch_id,
        file_name: import_batch?.file_name ?? null,
        created_at: new Date().toISOString(),
      },
      ...store.importBatches,
    ];
  }
  if (created.length) notifyMembersChanged();
  return Promise.resolve(
    envelope({
      items: created,
      rejected,
      created_count: created.length,
      rejected_count: rejected.length,
      import_batch_id,
    })
  );
};

const EDITABLE_FIELDS = [
  'first_name',
  'last_name',
  'job_title',
  'department',
  'role_ids',
  'group_ids',
];

/**
 * Edit profile fields, roles and groups of a member (email and status are not editable here).
 * TODO(CT-5563 / backend): `PATCH /people-access/members/:id` — contract TBD (ARP/backend).
 */
export const updateMember = (id, patch = {}) => {
  const member = findMember(id);
  if (!member) return Promise.resolve(notFoundEnvelope('Member not found'));
  const next = { ...member };
  EDITABLE_FIELDS.forEach((key) => {
    if (patch[key] !== undefined)
      next[key] = Array.isArray(patch[key]) ? [...patch[key]] : patch[key];
  });
  if (
    unknownIds(next.role_ids, store.roles).length ||
    unknownIds(next.group_ids, store.groups).length
  ) {
    return Promise.resolve(errorEnvelope(422, 'UNKNOWN_REFERENCE', 'Unknown role or group.'));
  }
  if ('first_name' in patch || 'last_name' in patch) {
    next.name = [next.first_name, next.last_name].filter(Boolean).join(' ');
  }
  store.members = store.members.map((m) => (m.id === id ? next : m));
  notifyMembersChanged();
  return Promise.resolve(envelope(decorateMember(next)));
};

const TRANSITIONS = {
  suspend: {
    from: ['active'],
    apply: () => ({ status: 'suspended', suspended_at: new Date().toISOString() }),
  },
  reactivate: {
    from: ['suspended', 'removed'],
    apply: () => ({ status: 'active', suspended_at: null, removed_at: null }),
  },
  remove: {
    from: ['active', 'invited', 'expired', 'not_invited', 'suspended'],
    apply: () => ({
      status: 'removed',
      removed_at: new Date().toISOString(),
      role_ids: [],
      group_ids: [],
    }),
  },
  revoke: { from: ['invited', 'expired'], apply: () => inviteFields(false) },
  resend: { from: ['invited', 'expired', 'not_invited'], apply: () => inviteFields(true) },
};

const applyMemberStatus = (id, action) => {
  const member = findMember(id);
  if (!member) return Promise.resolve(notFoundEnvelope('Member not found'));
  const transition = TRANSITIONS[action];
  if (!transition)
    return Promise.resolve(errorEnvelope(422, 'UNKNOWN_ACTION', `Unknown action: ${action}`));
  if (member.user_type === 'owner' && ['suspend', 'remove'].includes(action)) {
    return Promise.resolve(ownerForbidden(action === 'suspend' ? 'suspended' : 'removed'));
  }
  if (!transition.from.includes(member.status)) {
    return Promise.resolve(
      errorEnvelope(409, 'INVALID_TRANSITION', `Cannot ${action} a member who is ${member.status}.`)
    );
  }
  const next = { ...member, ...transition.apply() };
  if (action === 'remove') {
    // Soft delete: the person stays listed but keeps no access (roles, groups, direct grants).
    delete store.directGrants[id];
    delete store.studioAccess[id];
    delete store.memberResources[id];
    delete store.roleAssignments[id];
  }
  store.members = store.members.map((m) => (m.id === id ? next : m));
  notifyMembersChanged();
  return Promise.resolve(envelope(decorateMember(next)));
};

const REMOVAL_ACTIONS = ['remove', 'reactivate'];

/**
 * Lifecycle action on a member: suspend / reactivate / remove / revoke (invitation) / resend (invitation).
 * Remove and Reactivate answer to the `remove` QA switch (`?mockFail=remove`), like Delete user.
 * TODO(CT-5563 / backend): `POST /people-access/members/:id/status` — contract TBD (ARP/backend).
 */
export const setMemberStatus = (id, action) =>
  REMOVAL_ACTIONS.includes(action)
    ? withMockQa('remove', () => applyMemberStatus(id, action))
    : applyMemberStatus(id, action);

const ACTIVE_SESSION_WINDOW_DAYS = 7;

/**
 * What a member loses when removed from the workspace (step 1 of the Remove flow): studios,
 * roles, groups, directly-added permissions, shared resources and active sessions. Roles and
 * groups come from the member; studios from their effective grants (an Admin / Owner reaches
 * every studio by type). Shared resources are the distinct resources of the member (a few seeded
 * members have some) and a session counts as active when the member was seen in the last 7 days.
 * TODO(CT-5574 / backend): `GET /people-access/members/:id/removal-impact` — contract TBD (ARP/backend).
 */
export const getMemberRemovalImpact = async (id) => {
  const member = findMember(id);
  if (!member) return notFoundEnvelope('Member not found');
  const { body } = await getMemberStudioGrants(id);
  const privileged = member.user_type === 'owner' || member.user_type === 'admin';
  const studio_names = body.items
    .filter((studio) => privileged || studio.direct_access || studio.grants.length > 0)
    .map((studio) => studio.name);
  const seenDaysAgo = member.last_active_at
    ? (Date.now() - new Date(member.last_active_at).getTime()) / DAY
    : Infinity;
  return envelope({
    member_id: id,
    studio_names,
    role_names: decorateMember(member).roles.map((r) => r.name),
    group_names: decorateMember(member).groups.map((g) => g.name),
    direct_permissions: (store.directGrants[id] ?? []).length,
    // A resource reached by two paths counts once (type + name is its identity).
    shared_resources: new Set((store.memberResources[id] ?? []).map((r) => `${r.type}:${r.name}`)).size,
    active_sessions: seenDaysAgo <= ACTIVE_SESSION_WINDOW_DAYS ? 1 : 0,
  });
};

const removeMemberRecord = (id) => {
  const member = findMember(id);
  if (!member) return Promise.resolve(notFoundEnvelope('Member not found'));
  if (member.user_type === 'owner') return Promise.resolve(ownerForbidden('deleted'));
  store.members = store.members.filter((m) => m.id !== id);
  delete store.directGrants[id];
  delete store.memberResources[id];
  delete store.roleAssignments[id];
  notifyMembersChanged();
  return Promise.resolve(envelope({ id, deleted: true }));
};

/**
 * Hard-delete a member (e.g. discarding a not-invited row). The Owner is protected.
 * TODO(CT-5563 / backend): `DELETE /people-access/members/:id` — contract TBD (ARP/backend).
 */
export const deleteMember = (id) => withMockQa('remove', () => removeMemberRecord(id));

/**
 * Promote / demote between `admin` and `member`. The Owner never changes type.
 * TODO(CT-5563 / backend): `PATCH /people-access/members/:id/user-type` — contract TBD (ARP/backend).
 */
export const changeUserType = (id, user_type) => {
  const member = findMember(id);
  if (!member) return Promise.resolve(notFoundEnvelope('Member not found'));
  if (member.user_type === 'owner')
    return Promise.resolve(ownerForbidden('changed to another user type'));
  if (!ASSIGNABLE_USER_TYPES.includes(user_type)) {
    return Promise.resolve(
      errorEnvelope(422, 'INVALID_USER_TYPE', 'User type must be admin or member.')
    );
  }
  const next = { ...member, user_type };
  store.members = store.members.map((m) => (m.id === id ? next : m));
  notifyMembersChanged();
  return Promise.resolve(envelope(decorateMember(next)));
};

const RESET_LINK_HOURS = 1;
/** Whoever has signed in before can get a reset link. */
const PASSWORD_RESET_STATUSES = ['active', 'suspended'];

/**
 * Send a password-reset link to a member who can sign in.
 * TODO(CT-5611 / backend): `POST /people-access/members/:id/reset-password` — contract TBD (ARP/backend).
 */
export const resetMemberPassword = (id) => {
  const member = findMember(id);
  if (!member) return Promise.resolve(notFoundEnvelope('Member not found'));
  if (!PASSWORD_RESET_STATUSES.includes(member.status)) {
    return Promise.resolve(
      errorEnvelope(409, 'INVALID_STATE', `Cannot reset the password of a ${member.status} member.`)
    );
  }
  return Promise.resolve(envelope({ member_id: id, expires_in_hours: RESET_LINK_HOURS }));
};

/**
 * Correct the email of a member who has not accepted the invitation yet. The invitation goes out
 * again to the new address (status `invited`, a fresh expiry) and the link sent to the old one
 * stops working. A member who was never invited keeps that status: the
 * invitation has not gone out, so there is nothing to re-send.
 * TODO(CT-5611 / backend): `PATCH /people-access/members/:id/email` — contract TBD (ARP/backend).
 */
export const updateMemberEmail = (id, email) => {
  const member = findMember(id);
  if (!member) return Promise.resolve(notFoundEnvelope('Member not found'));
  if (!PENDING_STATUSES.includes(member.status)) {
    return Promise.resolve(
      errorEnvelope(409, 'INVALID_STATE', 'Only a pending member can have the email edited.')
    );
  }
  const next = String(email ?? '').trim();
  if (!EMAIL_RE.test(next)) {
    return Promise.resolve(errorEnvelope(422, 'INVALID_EMAIL', 'Enter a valid email address.'));
  }
  const taken = store.members.some(
    (m) => m.id !== id && m.email.toLowerCase() === next.toLowerCase()
  );
  if (taken) {
    return Promise.resolve(
      errorEnvelope(409, 'EMAIL_TAKEN', 'Another member already uses this email. Enter a different one.')
    );
  }
  const resends = member.status !== 'not_invited';
  const updated = { ...member, email: next, ...(resends ? inviteFields(true) : {}) };
  store.members = store.members.map((m) => (m.id === id ? updated : m));
  notifyMembersChanged();
  return Promise.resolve(envelope(decorateMember(updated)));
};

const CSV_COLUMNS = [
  'name',
  'email',
  'user_type',
  'status',
  'mfa_enabled',
  'last_active_at',
  'added_at',
];

const csvCell = (value) => {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Export the FULL member list as CSV (not the filtered view: provisional scope, CT-5609).
 * TODO(CT-5609 / backend): `GET /people-access/members/export` — contract TBD (ARP/backend).
 */
export const exportMembers = () => {
  const rows = store.members.map((m) => CSV_COLUMNS.map((key) => csvCell(m[key])).join(','));
  return Promise.resolve(
    envelope({
      csv: [CSV_COLUMNS.join(','), ...rows].join('\n'),
      file_name: `members-${new Date().toISOString().slice(0, 10)}.csv`,
    })
  );
};

/**
 * Send (or re-send) the invitation of a not-invited / invited / expired member.
 * TODO(CT-5564 / backend): `POST /people-access/members/:id/invitation` — contract TBD (ARP/backend).
 */
export const sendInvitation = (id) => withMockQa('invite', () => setMemberStatus(id, 'resend'));

/**
 * Import batches (bulk loads from a file), newest first, with how many members each created.
 * Manual invites create no batch.
 * TODO(CT-5638 / backend): `GET /people-access/members/import-batches` — contract TBD (ARP/backend, ARP-1480).
 */
export const listImportBatches = () =>
  Promise.resolve(
    envelope({
      items: store.importBatches.map((batch) => ({
        ...batch,
        member_count: store.members.filter((m) => m.import_batch_id === batch.id).length,
      })),
    })
  );

/**
 * One import batch with how many members it created and how many of them are still not invited
 * (the banner of the Members list). 404 when the batch does not exist.
 * Mock of `GET /people-access/members/import-batches/:id` (contract TBD, ARP-1480).
 */
export const getImportBatchSummary = (id) => {
  const batch = store.importBatches.find((b) => b.id === id);
  if (!batch) return Promise.resolve(notFoundEnvelope('Import batch not found'));
  const members = store.members.filter((m) => m.import_batch_id === id);
  return Promise.resolve(
    envelope({
      ...batch,
      member_count: members.length,
      not_invited_count: members.filter((m) => m.status === 'not_invited').length,
    })
  );
};

/**
 * Undo an import: the members it added are deleted and the batch disappears. Only while nobody
 * from the batch was invited (an email cannot be recalled); profile updates to existing members
 * are not touched. 404 for an unknown batch, 409 once someone was invited.
 * Mock of `DELETE /people-access/members/import-batches/:id` (contract TBD, ARP-1480).
 */
export const undoImportBatch = (id) => {
  const batch = store.importBatches.find((b) => b.id === id);
  if (!batch) return Promise.resolve(notFoundEnvelope('Import batch not found'));
  const members = store.members.filter((m) => m.import_batch_id === id);
  if (members.some((m) => m.status !== 'not_invited')) {
    return Promise.resolve(
      errorEnvelope(409, 'IMPORT_ALREADY_INVITED', 'Some people of this import were already invited.')
    );
  }
  store.members = store.members.filter((m) => m.import_batch_id !== id);
  store.importBatches = store.importBatches.filter((b) => b.id !== id);
  members.forEach((m) => delete store.directGrants[m.id]);
  notifyMembersChanged();
  return Promise.resolve(envelope({ batch_id: id, deleted_count: members.length }));
};

const MOCK_IMPORT_BATCH = Object.freeze({ id: 'batch-mock-import', file_name: 'q1-people.csv' });

/**
 * QA only (`?mockImport=1`, development builds): the batch a bulk import would have just created,
 * in the shape the import flow navigates to the Members list with. Null when the switch is off.
 */
export const getMockImportLanding = () =>
  isMockImportActive()
    ? {
        id: MOCK_IMPORT_BATCH.id,
        file: MOCK_IMPORT_BATCH.file_name,
        label: MOCK_IMPORT_BATCH.file_name,
        date: new Date().toISOString(),
      }
    : null;

// With the switch on, the store starts with two people from that batch (invited, so they show
// Invited + New, "Just now").
if (isMockImportActive()) {
  createMembers({
    members: [
      { email: 'juan@avance.com', first_name: 'Juan', last_name: '' },
      { email: 'maria@avance.com', first_name: 'María', last_name: '' },
    ],
    role_ids: store.roles.slice(0, 1).map((role) => role.id),
    send_invitation: true,
    import_batch: MOCK_IMPORT_BATCH,
  });
}
